import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Formik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import { Trans } from 'react-i18next';

import { variableFontRegular } from 'styles/theme/text';
import { Modal } from 'shared/components/Modal';
import { sharedValidationsMap } from 'shared/utils/validations';
import { GiftCardForm } from 'checkout/components/Payment/GiftCardForm';
import { GiftCardRemoveModal } from 'checkout/components/Payment/GiftCardRemoveModal';
import { useCurrency } from 'shared/hooks/useCurrency';
import { ChangeCurrencyGiftCardModal } from 'checkout/components/Payment/ChangeCurrencyGiftCardModal';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useGiftCard } from 'checkout/hooks/useGiftCard';
import { IGiftCard } from 'checkout/types/GiftCard';
import { IPartialOrder } from 'checkout/types/Order';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { guestPurchasingGiftCard, purchasingGiftCard } from 'checkout/utils/giftCards';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Payment.GiftCard';

export interface IGiftCardFormValues {
  giftCardCode: string;
  giftCardId: string;
  giftCardValue: number;
}

const initialValues: IGiftCardFormValues = {
  giftCardCode: '',
  giftCardId: '',
  giftCardValue: null,
};

export const GiftCard = ({ className }: { className?: string }) => {
  const router = useRouter();

  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [appliedGiftCards, setAppliedGiftCards] = useState<IGiftCard[]>([]);
  const [giftCardToBeRemoved, setGiftCardToBeRemoved] = useState('');
  const [showChangeCurrencyModal, setShowChangeCurrencyModal] = useState(false);

  const { selectedCurrencyCode } = useCurrency();
  const { dispatch, order, isGuestCheckout } = useCheckoutContext();
  const { isAuthenticated, user } = useStoreSelector((state) => state.auth);

  const { applyGiftCardToOrder, removeGiftCardFromOrder } = useGiftCard({ dispatch, order });

  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );
  const isWebEnableCheckoutGiftCardsAllowGuestPurchase = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_PURCHASE,
  );
  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isCheckoutGiftCardsEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS);
  const isWebEnableCheckoutGiftCardsAllowGuestRedemption = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_REDEMPTION,
  );

  const isGuestPurchasingGiftCard = guestPurchasingGiftCard({
    giftCardId: router?.query?.giftCardId,
    isAuthenticated,
    isGuestCheckout,
    isWebEnableCheckoutGiftCardsAllowGuestPurchase,
  });
  const isPurchasingGiftCard = purchasingGiftCard({
    giftCardId: order?.product?.giftCardId,
    isGuestPurchasingGiftCard,
    isCheckoutPurchaseGiftCardsEnabled,
  });
  const isOffer = order?.wantId && isCheckoutOffersEnabled;
  const showGiftCard =
    !isGuestCheckout ||
    !user?.isGuest ||
    ((isGuestCheckout || user?.isGuest) && isWebEnableCheckoutGiftCardsAllowGuestRedemption);

  const handleApplyGiftCard = async (formValues: IGiftCardFormValues) => {
    const response: IPartialOrder = await applyGiftCardToOrder(formValues?.giftCardId);

    const giftCard = response?.giftCards?.[0];

    sendTrackingEvent('cart_apply_gc_tap', {
      is_valid: !!giftCard,
      product_template_slug: order?.product?.productTemplate?.slug,
      condition_category: order?.product?.condition,
      size: order?.product?.size,
      price: order?.product?.priceCents,
      shoe_condition: order?.product?.shoeCondition,
      box_condition: order?.product?.boxCondition,
    });

    if (giftCard) {
      setAppliedGiftCards((current) => [...current, giftCard]);

      setShowAddModal(false);
    }
  };

  const handleCloseGiftCardCurrencyModal = ({ changedCurrency }) => {
    setShowChangeCurrencyModal(false);

    if (changedCurrency) {
      setShowAddModal(true);
    }
  };

  const handleOpenGiftCardModal = (event) => {
    event.preventDefault();

    sendTrackingEvent('cart_add_gc_tap', {
      product_template_slug: order?.product?.productTemplate?.slug,
      condition_category: order?.product?.condition,
      size: order?.product?.size,
      price: order?.product?.priceCents,
      shoe_condition: order?.product?.shoeCondition,
      box_condition: order?.product?.boxCondition,
    });
    setShowAddModal(true);
  };

  const handleOpenRemoveModal = (event, giftCardId) => {
    event.preventDefault();
    setGiftCardToBeRemoved(giftCardId);
  };

  const handleClickRemoveGiftCard = () => {
    void removeGiftCardFromOrder(giftCardToBeRemoved);

    sendTrackingEvent('cart_remove_gc_tap', {
      product_template_slug: order?.product?.productTemplate?.slug,
      condition_category: order?.product?.condition,
      size: order?.product?.size,
      price: order?.product?.priceCents,
      shoe_condition: order?.product?.shoeCondition,
      box_condition: order?.product?.boxCondition,
    });

    setAppliedGiftCards((current) =>
      current.filter((giftCard) => giftCard?.id !== giftCardToBeRemoved),
    );

    setGiftCardToBeRemoved('');
  };

  const handleClickCancelRemoveGiftCard = (event) => {
    event.preventDefault();
    setGiftCardToBeRemoved('');
  };

  useEffect(() => {
    if (!isEmpty(order?.giftCards)) {
      setAppliedGiftCards(order?.giftCards);
    }
  }, [order?.number]);

  if (isPurchasingGiftCard || isOffer || !isCheckoutGiftCardsEnabled || !showGiftCard) {
    return null;
  }

  return (
    <GiftCard.Wrapper className={className}>
      {appliedGiftCards.length > 0 &&
        appliedGiftCards.map((giftCard) => {
          if (!giftCard) return null;
          return (
            <GiftCard.AppliedGiftCards key={giftCard?.id}>
              <p>
                <Trans
                  i18nKey={`${TP}.appliedGiftCards`}
                  defaults="<0>GOAT Gift Card *{giftCardCode}</0><1 /><0>{total} applied</0>"
                  components={[<span />, <br />]}
                  values={{
                    giftCardCode: giftCard?.codeLast4,
                    total: formatMoneyObject(
                      {
                        currency: selectedCurrencyCode,
                        amount:
                          selectedCurrencyCode === 'USD'
                            ? giftCard?.localizedAmountToUseCents?.amountUsdCents
                            : giftCard?.localizedAmountToUseCents?.amount,
                      },
                      {
                        showCents: true,
                        displayCurrencySymbol: true,
                      },
                    ),
                  }}
                />
              </p>
              <GiftCard.RemoveGiftCardButton
                href=""
                onClick={(event) => handleOpenRemoveModal(event, giftCard?.id)}
              >
                {t(`${TP}.giftCardAppliedRemove`, 'Remove')}
              </GiftCard.RemoveGiftCardButton>
            </GiftCard.AppliedGiftCards>
          );
        })}
      {/* NOTE: restrict to one gift card only */}
      {appliedGiftCards.length === 0 && (
        <GiftCard.AddGiftCardButton href="" onClick={handleOpenGiftCardModal}>
          {t(`${TP}.giftCardAddNew`, '+ Add GOAT Gift Card')}
        </GiftCard.AddGiftCardButton>
      )}

      {showAddModal && (
        <GiftCard.GiftCardModal
          onClose={() => setShowAddModal(false)}
          title={t(`${TP}.giftCardModalTitle`, 'GOAT Gift Card')}
        >
          <GiftCard.ModalSubtitle>
            {t(
              `${TP}.giftCardVerificationInstructions`,
              'To apply your balance, please enter the code associated with your GOAT Gift Card.',
            )}
          </GiftCard.ModalSubtitle>
          <Formik
            validateOnChange={false}
            validateOnBlur
            initialValues={initialValues}
            onSubmit={handleApplyGiftCard}
            validationSchema={sharedValidationsMap.giftCardValidationSchema()}
          >
            <GiftCardForm />
          </Formik>
        </GiftCard.GiftCardModal>
      )}

      {Boolean(giftCardToBeRemoved) && (
        <GiftCardRemoveModal
          onClickClose={(event) => handleClickCancelRemoveGiftCard(event)}
          onClickRemove={handleClickRemoveGiftCard}
        />
      )}

      {showChangeCurrencyModal && (
        <ChangeCurrencyGiftCardModal onClose={handleCloseGiftCardCurrencyModal} />
      )}
    </GiftCard.Wrapper>
  );
};

GiftCard.AppliedGiftCards = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 15px;
  width: 315px;
`;

GiftCard.RemoveGiftCardButton = styled.a`
  text-decoration: underline;
`;

GiftCard.AddGiftCardButton = styled.a`
  text-decoration: underline;
  ${variableFontRegular({ size: '11.5px', height: '11.5px' })}
`;

GiftCard.GiftCardModal = styled(Modal)`
  width: 200px;
`;

GiftCard.ModalSubtitle = styled.p`
  ${variableFontRegular({ size: '9.5px' })};
  margin-top: 10px;
`;

GiftCard.Wrapper = styled.div`
  padding-bottom: 48px;
`;
